import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, FileText, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import api from '../../lib/api';

const PDFUploader = ({
  lessonId = null,
  onUploadSuccess,
  onUploadError,
  maxSizeMB = 50,
  language = 'fr',
  className = ''
}) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [uploadedPdf, setUploadedPdf] = useState(null);
  const fileInputRef = useRef(null);

  const t = (fr, en) => (language === 'fr' ? fr : en);

  const validateFile = useCallback((selectedFile) => {
    const fileName = selectedFile.name.toLowerCase();
    if (!fileName.endsWith('.pdf')) {
      return t('Seuls les fichiers PDF sont acceptés', 'Only PDF files are allowed');
    }
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (selectedFile.size > maxSizeBytes) {
      const fileSizeMB = (selectedFile.size / (1024 * 1024)).toFixed(1);
      return t(
        `Fichier trop volumineux (${fileSizeMB} MB). Maximum: ${maxSizeMB} MB`,
        `File too large (${fileSizeMB} MB). Max: ${maxSizeMB} MB`
      );
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxSizeMB, language]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    const validationError = validateFile(selectedFile);
    if (validationError) { setError(validationError); return; }
    setFile(selectedFile);
    setError('');
    setUploadedPdf(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files?.[0];
    if (!droppedFile) return;
    const validationError = validateFile(droppedFile);
    if (validationError) { setError(validationError); return; }
    setFile(droppedFile);
    setError('');
    setUploadedPdf(null);
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };

  const CHUNK_SIZE = 2 * 1024 * 1024; // 2 MB — passe sous les limites de proxy

  const pollUploadStatus = async (uploadId) => {
    for (let attempt = 0; attempt < 300; attempt++) {
      await new Promise(r => setTimeout(r, 2000));
      const res = await api.get(`/videos/upload-status/${uploadId}`);
      if (res.data.status === 'done') return res.data.result;
      if (res.data.status === 'error') throw new Error(res.data.error || 'Échec du stockage');
    }
    throw new Error('Délai de traitement dépassé');
  };

  const uploadPdf = async () => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    setError('');

    try {
      const uploadId = (window.crypto?.randomUUID)
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
      const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
      let response = null;

      for (let i = 0; i < totalChunks; i++) {
        const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const formData = new FormData();
        formData.append('file', blob, file.name);
        formData.append('upload_id', uploadId);
        formData.append('chunk_index', i);
        formData.append('total_chunks', totalChunks);
        formData.append('filename', file.name);
        formData.append('kind', 'pdf');
        formData.append('directory', 'pdfs');
        if (lessonId) formData.append('lesson_id', lessonId);

        response = await api.post('/videos/upload-chunk', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setProgress(Math.round(((i + 1) / totalChunks) * 100));
      }

      let resultData = response.data;
      if (resultData.processing) {
        resultData = await pollUploadStatus(uploadId);
      }

      setUploadedPdf({ ...resultData, original_filename: file.name });
      setFile(null);
      if (onUploadSuccess) onUploadSuccess(resultData);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || t('Échec de l\'upload', 'Upload failed');
      setError(errorMessage);
      if (onUploadError) onUploadError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    setError('');
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatFileSize = (bytes) =>
    bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Drop Zone */}
      {!file && !uploadedPdf && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50/50 transition-colors"
          data-testid="pdf-dropzone"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="pdf-file-input"
          />
          <Upload className="w-10 h-10 mx-auto text-slate-400 mb-3" />
          <p className="text-base font-medium text-slate-700 mb-1">
            {t('Glissez-déposez votre PDF ici', 'Drag and drop your PDF here')}
          </p>
          <p className="text-sm text-slate-500 mb-2">
            {t('ou cliquez pour sélectionner un fichier', 'or click to select a file')}
          </p>
          <p className="text-xs text-slate-400">
            {t(`PDF uniquement • Maximum: ${maxSizeMB} MB`, `PDF only • Max: ${maxSizeMB} MB`)}
          </p>
        </div>
      )}

      {/* Selected File */}
      {file && !uploading && !uploadedPdf && (
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
          <div className="flex items-center space-x-3">
            <FileText className="w-7 h-7 text-blue-600" />
            <div>
              <p className="font-medium text-slate-800 truncate max-w-xs">{file.name}</p>
              <p className="text-sm text-slate-500">{formatFileSize(file.size)}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button onClick={clearFile} variant="ghost" size="sm" className="text-slate-500 hover:text-red-500" data-testid="pdf-clear-btn">
              <X className="w-4 h-4" />
            </Button>
            <Button onClick={uploadPdf} className="bg-blue-600 hover:bg-blue-700" data-testid="pdf-upload-btn">
              <Upload className="w-4 h-4 mr-2" />
              {t('Téléverser', 'Upload')}
            </Button>
          </div>
        </div>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center space-x-3 mb-3">
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            <span className="font-medium text-blue-700">
              {t('Téléversement en cours...', 'Uploading...')}
            </span>
            <span className="text-sm text-blue-600">{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      )}

      {/* Upload Success */}
      {uploadedPdf && (
        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-green-700">
                {t('PDF téléversé avec succès!', 'PDF uploaded successfully!')}
              </p>
              <p className="text-sm text-green-600 truncate">{uploadedPdf.original_filename || (uploadedPdf.cdn_url || '').split('/').pop()}</p>
            </div>
            <Button
              onClick={() => { setUploadedPdf(null); clearFile(); }}
              variant="outline"
              size="sm"
              className="border-green-300 text-green-700 hover:bg-green-100"
              data-testid="pdf-upload-another-btn"
            >
              {t('Remplacer', 'Replace')}
            </Button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-50 rounded-lg border border-red-200">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-600" data-testid="pdf-error">{error}</p>
        </div>
      )}
    </div>
  );
};

export default PDFUploader;
